import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function DelayLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const panelBg = "rgba(255,255,255,0.04)";
  const darkPanel = "rgba(0,0,0,0.18)";

  const articleSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Delay in Music Production: What It Is and How It Works",
      description:
        "Learn what delay is, how delay effects work, the history of delay, common types of delay, and practical ways to use delay in music production.",
      author: {
        "@type": "Organization",
        name: "Parish Audio",
      },
      publisher: {
        "@type": "Organization",
        name: "Parish Audio",
        logo: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/assets/logo.png",
        },
      },
      mainEntityOfPage: "https://parishaudio.com/learning/delay",
      articleSection: "Audio Education",
      keywords: [
        "delay effect",
        "audio delay",
        "echo effect",
        "tape delay",
        "digital delay",
        "ping pong delay",
      ],
    }),
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "Delay repeats a sound after a short amount of time, creating echoes, space, movement, and rhythm.",
      icon: <AccessTimeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "Delay can add depth, groove, stereo width, and atmosphere without washing a sound out like reverb sometimes can.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Vocals, guitars, synths, drums, sound design, ambient textures, and rhythmic effects.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const delayTypes = [
    {
      name: "Digital Delay",
      description:
        "Digital delay captures the signal in memory and plays it back after a precise amount of time. It is clean, flexible, and capable of very accurate rhythmic repeats.",
    },
    {
      name: "Tape Delay",
      description:
        "Tape delay records the input to moving magnetic tape and then plays it back later. It is known for warmth, saturation, soft repeats, and the movement caused by wow and flutter.",
    },
    {
      name: "Ping Pong Delay",
      description:
        "Ping pong delay alternates repeats between the left and right channels, creating a wide stereo image and a sense of motion.",
    },
    {
      name: "Multi-Tap Delay",
      description:
        "Multi-tap delay uses several repeat points at different times, allowing for complex patterns, rhythmic textures, and layered echoes.",
    },
    {
      name: "Slapback Delay",
      description:
        "A very short single repeat, often used on vocals and guitars for thickness, presence, and vintage-style space.",
    },
    {
      name: "Analog / Bucket Brigade Delay",
      description:
        "Often darker and less pristine than digital delay, with repeats that soften as they continue, giving a more vintage or organic character.",
    },
  ];

  const parameters = [
    {
      name: "Time",
      description:
        "Sets how long it takes before the repeat is heard. This can be measured in milliseconds or synced to musical note values.",
    },
    {
      name: "Feedback",
      description:
        "Controls how many repeats occur. Higher feedback means more echoes and a longer decay trail.",
    },
    {
      name: "Mix",
      description:
        "Balances the dry signal with the delayed signal. Lower mix settings are subtle, while higher settings make delay more obvious.",
    },
    {
      name: "Tone / Filtering",
      description:
        "Shapes the character of the repeats. Darker repeats feel more vintage and sit back in the mix, while brighter repeats feel clearer and more forward.",
    },
    {
      name: "Stereo Width / Pan",
      description:
        "Controls how the repeats are placed across the stereo field, especially in stereo and ping pong styles.",
    },
    {
      name: "Modulation",
      description:
        "Adds movement to the repeats, often making them feel softer, wider, or more tape-like.",
    },
  ];

  const bestPractices = [
    "Use shorter delay times when you want thickness or slapback instead of obvious echoes.",
    "Sync delay time to the song tempo when you want the repeats to reinforce the groove.",
    "Filter the repeats so the original signal stays clear while the echoes sit behind it.",
    "Use ping pong or stereo delay carefully so it adds width without distracting from the center of the mix.",
    "Watch feedback settings, especially on live inputs, because high feedback can build quickly and overwhelm a mix.",
  ];

  return (
    <>
      <Seo
        title="Delay | Parish Audio Learning"
        description="Learn what delay is, how delay effects work, the history of delay, common types of delay, and practical ways to use delay in music production."
        canonical="https://parishaudio.com/learning/delay"
        schema={articleSchema}
      />

      <Box
        sx={{
          minHeight: height - 130,
          backgroundImage: `linear-gradient(rgba(0,0,0,0.72), rgba(0,0,0,0.72)), url("/RecordingStudioStockImage1.jpg")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          py: 3,
        }}
      >
        <Box sx={{ width: "95%", maxWidth: 1100, mx: "auto" }}>
          <Paper
            sx={{
              p: { xs: 2, md: 3 },
              background: `linear-gradient(180deg, ${style.color1} 0%, rgba(22,32,48,0.98) 100%)`,
              borderRadius: 3,
              color: style.color4,
              boxShadow: 12,
            }}
          >
            <Stack spacing={3}>
              <Box ref={topContainer}>
                <Chip
                  label="Learning Center"
                  sx={{
                    mb: 1.5,
                    color: style.color4,
                    backgroundColor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                />

                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2rem", md: "3rem" },
                    fontWeight: 800,
                    lineHeight: 1.05,
                    mb: 1.25,
                    color: style.color4,
                  }}
                >
                  Delay
                </Typography>

                <Typography
                  sx={{
                    color: style.color3,
                    fontSize: { xs: "1rem", md: "1.1rem" },
                    lineHeight: 1.75,
                    maxWidth: 920,
                  }}
                >
                  Delay is one of the most versatile effects in music production.
                  At its core, it creates a repeat of a sound after a short amount
                  of time. That simple idea can produce everything from a subtle
                  thickening effect to spacious ambience, rhythmic echoes, stereo
                  movement, and highly creative sound design.
                </Typography>
              </Box>

              <Grid container spacing={2} alignItems="stretch">
                {quickFacts.map((item) => (
                  <Grid item xs={12} md={4} key={item.title} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        backgroundColor: panelBg,
                        boxShadow: 6,
                        width: "100%",
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="flex-start">
                        {item.icon}
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: style.color4,
                              mb: 0.4,
                            }}
                          >
                            {item.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.6 }}>
                            {item.text}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Paper
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 3,
                  backgroundColor: panelBg,
                  boxShadow: 8,
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccessTimeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      variant="h2"
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      What Is Delay?
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Delay works by storing a copy of the incoming signal and playing
                    it back later, blended with the original sound. Because the repeat
                    happens after the initial note or phrase, the listener hears an
                    echo-like effect.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Very short delay times can make a sound feel thicker or wider.
                    Medium and long delay times produce clear repeats that can create
                    groove, depth, and atmosphere. Depending on the settings, delay can
                    be subtle and supportive or bold and attention-grabbing.
                  </Typography>
                </Stack>
              </Paper>

              <Paper
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 3,
                  backgroundColor: panelBg,
                  boxShadow: 8,
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TimelineRoundedIcon sx={{ color: accent }} />
                    <Typography
                      variant="h2"
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      A Brief History of Delay
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Early delay techniques appeared in broadcasting and studio
                    experiments long before modern pedals and plugins. Engineers found
                    ways to create short time offsets by routing audio through long
                    signal paths, tape systems, and other physical media.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    In the 1950s, reel-to-reel tape delays became an important part of
                    recorded music. By recording to magnetic tape and playing the sound
                    back moments later, engineers created repeats with a warm and lively
                    character. Tape speed, head spacing, and feedback all shaped the
                    sound.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Digital delay later brought far more precision and flexibility.
                    Instead of relying on moving mechanical parts, digital systems could
                    store and replay audio with excellent control over time, feedback,
                    filtering, and synchronization. Today, digital delay is common in
                    pedals, rack units, and plugins, while tape and analog styles remain
                    popular for their personality.
                  </Typography>
                </Stack>
              </Paper>

              <Grid container spacing={2.25} alignItems="stretch">
                <Grid item xs={12} md={7} sx={{ display: "flex" }}>
                  <Paper
                    sx={{
                      p: { xs: 2, md: 2.5 },
                      borderRadius: 3,
                      backgroundColor: panelBg,
                      boxShadow: 8,
                      width: "100%",
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                        <Typography
                          variant="h2"
                          sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                        >
                          Types of Delay
                        </Typography>
                      </Stack>

                      <Stack spacing={1.2}>
                        {delayTypes.map((type) => (
                          <Paper
                            key={type.name}
                            sx={{
                              p: 1.5,
                              borderRadius: 2.5,
                              backgroundColor: darkPanel,
                              boxShadow: 3,
                            }}
                          >
                            <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                              {type.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                              {type.description}
                            </Typography>
                          </Paper>
                        ))}
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={5} sx={{ display: "flex" }}>
                  <Paper
                    sx={{
                      p: { xs: 2, md: 2.5 },
                      borderRadius: 3,
                      backgroundColor: panelBg,
                      boxShadow: 8,
                      width: "100%",
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TuneRoundedIcon sx={{ color: accent }} />
                        <Typography
                          variant="h2"
                          sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                        >
                          Common Delay Controls
                        </Typography>
                      </Stack>

                      <Stack spacing={1.2}>
                        {parameters.map((param) => (
                          <Paper
                            key={param.name}
                            sx={{
                              p: 1.5,
                              borderRadius: 2.5,
                              backgroundColor: darkPanel,
                              boxShadow: 3,
                            }}
                          >
                            <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                              {param.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                              {param.description}
                            </Typography>
                          </Paper>
                        ))}
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              <Grid container spacing={2.25} alignItems="stretch">
                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                  <Paper
                    sx={{
                      p: { xs: 2, md: 2.5 },
                      borderRadius: 3,
                      backgroundColor: panelBg,
                      boxShadow: 8,
                      width: "100%",
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                        <Typography
                          variant="h2"
                          sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                        >
                          Best Practices
                        </Typography>
                      </Stack>

                      <Box
                        component="ul"
                        sx={{
                          pl: 2.5,
                          m: 0,
                          color: style.color3,
                          "& li": { mb: 1.15, lineHeight: 1.75 },
                        }}
                      >
                        {bestPractices.map((tip, index) => (
                          <li key={index}>{tip}</li>
                        ))}
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                  <Paper
                    sx={{
                      p: { xs: 2, md: 2.5 },
                      borderRadius: 3,
                      backgroundColor: panelBg,
                      boxShadow: 8,
                      width: "100%",
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                        <Typography
                          variant="h2"
                          sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                        >
                          Final Thoughts
                        </Typography>
                      </Stack>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        Delay is one of the most flexible effects available. It can be
                        used for subtle space, rhythmic interplay, stereo width, or
                        dramatic creative effects. The same basic idea — playing back a
                        sound later — can serve both practical mixing goals and bold
                        artistic ones.
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        Once you understand time, feedback, mix, and tonal shaping,
                        delay becomes much easier to control musically. Small changes
                        can produce very different emotional results.
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              <Paper
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: "rgba(255,255,255,0.03)",
                  boxShadow: 4,
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                >
                  <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                    Related reading: explore{" "}
                    <Box
                      component={RouterLink}
                      to="/learning/reverb"
                      sx={{ color: style.color4, textDecoration: "none", fontWeight: 700 }}
                    >
                      reverb
                    </Box>{" "}
                    and{" "}
                    <Box
                      component={RouterLink}
                      to="/learning/chorus"
                      sx={{ color: style.color4, textDecoration: "none", fontWeight: 700 }}
                    >
                      chorus
                    </Box>
                    .
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      component={RouterLink}
                      to="/learning/reverb"
                      variant="outlined"
                      sx={{
                        borderColor: accent,
                        color: style.color4,
                        fontWeight: 700,
                        "&:hover": {
                          borderColor: style.color4,
                          backgroundColor: "rgba(255,255,255,0.04)",
                        },
                      }}
                    >
                      Reverb
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/learning/chorus"
                      variant="outlined"
                      sx={{
                        borderColor: accent,
                        color: style.color4,
                        fontWeight: 700,
                        "&:hover": {
                          borderColor: style.color4,
                          backgroundColor: "rgba(255,255,255,0.04)",
                        },
                      }}
                    >
                      Chorus
                    </Button>
                  </Stack>
                </Stack>
              </Paper>

              <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                References: Historical discussions of delay often mention early
                broadcast line-delay techniques, reel-to-reel tape echo systems,
                and early digital products such as Eventide delay units. Those
                milestones helped shape the delay workflows still used in modern
                music production.
              </Typography>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </>
  );
}


// import {React, useState, useContext, useEffect, useRef} from "react"
// import {useStyle} from '../../context/styleContext/styleContext'
// import ReactGA from 'react-ga4';
// import { Paper } from '@mui/material';
// import { Container, Typography, Divider, Box, List, ListItem, ListItemText } from "@mui/material";


// export default function DelayLearning(){

//   const { style, setStyle } = useStyle();
//   const [width, setWidth] = useState(window.innerWidth)

// function handleWindowSizeChange() {
//     setWidth(window.innerWidth);
// }
// useEffect(() => {
//     window.addEventListener('resize', handleWindowSizeChange);
//     return () => {
//         window.removeEventListener('resize', handleWindowSizeChange);
//     }
// }, []);

// const isMobile = width <= 768;

// useEffect(() => {
//   document.title = 'Parish Audio | Cutting-Edge Audio Processing | Delay';
// }, []);

// ReactGA.send({
//     hitType:"pageview",
//     page:"learning/delay",
//     title:"learning/delay",
//   })

//   const [height, setHeight] = useState(window.innerHeight)

//   useEffect(() => {
//     function handleResize() {      
//       setWidth(window.innerWidth)
//       setHeight(window.innerHeight)
//   }
  
//     window.addEventListener('resize', handleResize)
//   })

// const topContainer = useRef();
//     useEffect(() => {
//         // To make sure page starts from the top
//         topContainer.current.scrollIntoView({ block: "end" });
//     }, []);




//     return(
//         <>
//         <div style={{display:'flex', backgroundImage: `url("/RecordingStudioStockImage1.jpg")`, backgroundSize: "cover", backgroundPosition: "center", marginBottom:'0px', flex:1, minHeight:height-130, color:style.color4, padding:'0px'}}>
//         <div style={{display:'flex', flex:1}}>

//         <div className="dark-alpha-80" style={{flex:1}}>
// <div style={{width:'95%', maxWidth:'1000px', margin:'auto'}}>



//         <h1 ref={topContainer} style={{marginTop:'20px'}}>Delay</h1>

//         <Divider className="mobile-card-divider" />
//       <p variant="body1" sx={{ color: "#dddddd", fontSize: "1.1rem" }}>
//         Delay is an effect that sounds similar to an echo. This is achieved by storing a copy of the signal and playing it back act
//         a later time, blended in with the current signal. 
//       </p>
//       <h2 style={{marginTop:'25px', marginBottom:'0px'}}>History</h2>
//       <p>Delay was first used by Radio broadcasters in the 1930s, using a telephone line. The audio signal was sent over the phone line to 
//         a town located several hunderd miles away, and then was sent back to studio. This created a delay in the return signal
//         of a few milliseconds, and the delayed signal could then be mixed back in<sup><a href="#cite1">[1]</a></sup>.

//       </p>
//       <p>The 1950's saw the rise of Reel to Reel tape delays, which were made from a loop of magnetic tape played over a reel to reel system. The input signal would be recorded 
//         to the tape, and the length of the tape loop as well as the position of the heads could be adjusted to change the delay time, volume, and other parameters. The sound 
//         that was recorded on the tape would then be played back at the desired time and mixed back into the signal.
//       </p>

//       <p>Digital delay first became available in 1971 with the <a href='https://www.eventideaudio.com/' target="_blank" rel="noopener noreferrer"><strong>Eventide</strong></a> DDL 1745. The first digital Delay effects were extremely
//       expensive, with the DDL 1745 retailing for $4000 ($28,000 adjusted for inflation)<sup><a href="#cite2">[2]</a></sup>. Digital delay offers extreme precision and flexibility, and has become the most popular
//       type of delay in use today.</p>
//       <h2 style={{marginTop:'30px'}}>Types</h2>
//       <div style={{padding:'0px', marginTop:'20px'}}>
//         <h3 style={{marginTop:'0px', marginBottom:'10px'}}>
//                 Ping Pong Delay
//         </h3>
//         <p style={{marginBottom:'10px', marginTop:'0px'}}>
//         A ping-pong delay causes the delayed signal to alternate between the left and right channels. This can help widen the stereo imaging of your track while also enhancing depth and movement.
//         </p>

//       </div>
//       <div>
//       <h3 style={{marginTop:'20px', marginBottom:'10px'}}>
//           Tape Delay
//       </h3>
//       <p style={{marginBottom:'10px', marginTop:'0px'}}>
//       First becoming available in the 1950's, a tape delay works by recording an incoming audio signal onto a loop of magnetic tape, then playing it back a short time later. The delay time can be adjusted by changing the distance between the read and write heads, or by changing the speed of the tape.  is determined by the distance between the record and playback heads and the speed at which the tape moves. Magnetic tape naturally introduces slight variations in speed (wow and flutter) and subtle saturation, the effect has a warm, organic character.
//       </p>
//       </div>
//       <div>
//       <h3 style={{marginTop:'20px', marginBottom:'10px'}}>
//             Multi Tap Delay
//       </h3>
//       <p style={{marginBottom:'15px', marginTop:'0px'}}>
//             Multi Tap delay allows the user to use multiple delays and adjust the timing and settings separately. This allows the
//             creation of complex rhythms and patterns.
//       </p>
//       </div>
//       <div>
//       <h3 style={{marginTop:'20px', marginBottom:'10px'}}>
//             Digital Delay
//       </h3>
//       <p style={{marginBottom:'15px', marginTop:'0px'}}>
//       A digital delay records the input signal digitally, stores it in memory, and then plays it back after a set time, mixed in with the current signal. Introduced in 1971, digital delays are known for their flexability and accuracy.
//       </p>
//       </div>
      
    
//       <Divider className="mobile-card-divider" />
//       <div style={{marginBottom:'25px', marginTop:'50px'}}>
//       <h2>References</h2>
//       <p id="cite1"> <a href="https://soundbetter.com/kb/delay-a-history-analysis" target="_blank" rel="noopener noreferrer">[1] Fectoper, B. (n.d.). <i>Delay: A History & Analysis</i>. SoundBetter. https://soundbetter.com/kb/delay-a-history-analysis</a></p>
//       <p id="cite2">[2] <a href="https://www.eventideaudio.com/50th-flashback-2-1-the-ddl-1745-delay/" target="_blank" rel="noopener noreferrer">AAgnello. (2023, June 8). <i>50th Flashback #2.1: The DDL 1745 Delay</i>. Eventide Audio.</a></p>


//       </div>

//       </div>
// </div>
//   </div>
// </div>



        
//         </>

//     )
// }